/**
 * CR-6247: expanded company field set for the Organisation Structure edit
 * dialog — Company Number, Number of Employees, Year Established, Currency and
 * Annual Revenue. Mirrors the supplier profile's Company Information block,
 * minus the supplier code (which is per-customer, not per-company).
 */
import { useEffect, useState } from 'react';
import { Banknote, Building2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { preferencesAPI } from '../../lib/api';
import type { NodeConfigForm } from './useNodeConfigForm';

/** Fallback list used only if the choices endpoint is unavailable. */
const FALLBACK_CURRENCIES = [
  { value: 'GBP', label: 'GBP' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

interface CompanyDetailsSectionProps {
  form: NodeConfigForm;
  onChange: <K extends keyof NodeConfigForm>(field: K, value: NodeConfigForm[K]) => void;
  /**
   * True when this site takes its regional settings from the group. Choosing a
   * currency here creates an override, so the field says so rather than
   * silently detaching the site from its group defaults.
   */
  inheritsCurrency: boolean;
}

/**
 * Company identity and business-detail inputs.
 *
 * @param form - current dialog form values
 * @param onChange - single-field setter from {@link useNodeConfigForm}
 * @param inheritsCurrency - whether the currency shown is a group default
 */
export function CompanyDetailsSection({
  form,
  onChange,
  inheritsCurrency,
}: CompanyDetailsSectionProps) {
  const [currencies, setCurrencies] = useState<Array<{ value: string; label: string }>>(
    FALLBACK_CURRENCIES,
  );

  // Same source the Regional Settings dropdowns use, so the two never disagree
  // about which currencies the platform supports.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await preferencesAPI.getPreferencesChoices();
        if (!cancelled && resp.data.currencies?.length) {
          setCurrencies(resp.data.currencies);
        }
      } catch {
        // Keep the fallback list — a missing choices call must not block the
        // rest of the dialog from saving.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <Building2 className="h-4 w-4" />
        Company Details
      </h3>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="company-number">Company Number</Label>
          <Input
            id="company-number"
            value={form.registrationNumber}
            onChange={(e) => onChange('registrationNumber', e.target.value)}
            placeholder="Registered company number"
          />
        </div>

        <div>
          <Label htmlFor="employee-count">Number of Employees</Label>
          <Input
            id="employee-count"
            type="number"
            min={0}
            value={form.employeeCount}
            onChange={(e) => onChange('employeeCount', e.target.value)}
            placeholder="e.g. 250"
          />
        </div>

        <div>
          <Label htmlFor="year-established">Year Established</Label>
          <Input
            id="year-established"
            type="number"
            min={1800}
            max={new Date().getFullYear()}
            value={form.yearEstablished}
            onChange={(e) => onChange('yearEstablished', e.target.value)}
            placeholder="e.g. 1990"
          />
        </div>

        <div>
          <Label htmlFor="company-currency" className="flex items-center gap-2">
            <Banknote className="h-3 w-3" /> Currency
          </Label>
          <Select
            value={form.currency}
            onValueChange={(value) => onChange('currency', value)}
          >
            <SelectTrigger id="company-currency">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {inheritsCurrency && (
            <p className="mt-1 text-xs text-muted-foreground">
              Currently inherited from the group. Changing it sets an override for this site.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="annual-revenue">Annual Revenue</Label>
          <Input
            id="annual-revenue"
            type="number"
            min={0}
            step="0.01"
            value={form.annualRevenue}
            onChange={(e) => onChange('annualRevenue', e.target.value)}
            placeholder="e.g. 5000000"
          />
        </div>
      </div>
    </div>
  );
}
